import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Card, PageHeader, Button, Input, Table, Th, Td } from "../components/ui";
import { fmtIDR, fmtDate, errMsg } from "../lib/utils";
import { toast } from "sonner";
import { Download, Loader2, Search } from "lucide-react";
import * as XLSX from "xlsx";

const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const BUCKET = { orders: "Orders", ny_po: "NY PO", outstanding: "Outstanding", paid: "Paid" };
const BUCKET_BADGE = { ny_po: "bg-amber-100 text-amber-800", outstanding: "bg-blue-100 text-blue-800", paid: "bg-emerald-100 text-emerald-800" };

export default function PipelineDetail() {
  const [sp] = useSearchParams();
  const ym = sp.get("period_month") || "";
  const bucket = sp.get("bucket") || "orders";
  const customerId = sp.get("customer_id") || "";
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const title = `${BUCKET[bucket] || bucket} — ${ym ? `${BULAN[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}` : ""}`;

  useEffect(() => {
    document.title = `Pipeline ${title}`;
    api.get("/reports/receivable-pipeline/detail", { params: { period_month: ym, bucket, ...(customerId ? { customer_id: customerId } : {}) } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [ym, bucket, customerId]); // eslint-disable-line

  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? data.rows.filter((r) => `${r.awb_number} ${r.customer_name || ""} ${r.invoice_number || ""}`.toLowerCase().includes(t)) : data.rows;
  }, [data.rows, q]);
  const total = rows.reduce((s, r) => s + r.value, 0);

  const exportXLS = () => {
    const ws = XLSX.utils.json_to_sheet(rows.map((r) => ({
      "No AWB": r.awb_number, Customer: r.customer_name || "-", Rute: `${r.origin || ""}→${r.destination || ""}`,
      "Tgl Order": fmtDate(r.created_at), Status: r.status, Kategori: BUCKET[r.bucket], "No Invoice": r.invoice_number || "-",
      "Tgl Print": r.printed_at ? fmtDate(r.printed_at) : "-", Nilai: r.value,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Detail");
    XLSX.writeFile(wb, `Pipeline_${bucket}_${ym}.xlsx`);
  };

  return (
    <div data-testid="pipeline-detail-page">
      <PageHeader title={`Detail ${title}`} />
      <div className="flex flex-wrap items-end gap-3 mb-4">
        {data.customer_name && <div className="text-sm">Customer: <b data-testid="pipeline-detail-customer">{data.customer_name}</b></div>}
        <div className="relative w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
          <Input data-testid="pipeline-detail-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari AWB / customer / invoice..." className="pl-9"/>
        </div>
        <Button data-testid="pipeline-detail-export" variant="secondary" onClick={exportXLS}><Download size={14}/> Export Excel</Button>
        <div className="ml-auto text-right">
          <div className="text-[10px] uppercase font-bold text-slate-500">{rows.length} AWB</div>
          <div data-testid="pipeline-detail-total" className="font-heading font-black text-xl">{fmtIDR(total)}</div>
        </div>
      </div>
      <Card className="p-0 overflow-hidden">
        <Table>
          <thead><tr>
            <Th>No AWB</Th><Th>Customer</Th><Th>Rute</Th><Th>Tgl Order</Th><Th>Status</Th><Th>No Invoice</Th><Th>Tgl Print</Th><Th className="text-right">Nilai</Th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.awb_id} data-testid={`pipeline-detail-row-${r.awb_number}`}>
                <Td className="font-mono font-bold">{r.awb_number}</Td>
                <Td>{r.customer_name || "-"}</Td>
                <Td className="text-xs text-slate-500">{r.origin}→{r.destination}</Td>
                <Td className="whitespace-nowrap">{fmtDate(r.created_at)}</Td>
                <Td><span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase whitespace-nowrap ${BUCKET_BADGE[r.bucket]}`}>{r.status} · {BUCKET[r.bucket]}</span></Td>
                <Td className="font-mono text-xs">{r.invoice_number || "-"}</Td>
                <Td className="whitespace-nowrap">{r.printed_at ? fmtDate(r.printed_at) : "-"}</Td>
                <Td className="text-right font-mono font-bold whitespace-nowrap">{fmtIDR(r.value)}</Td>
              </tr>
            ))}
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-6">Tidak ada AWB.</td></tr>}
            {loading && <tr><td colSpan={8} className="text-center text-slate-400 py-6"><Loader2 size={14} className="animate-spin inline"/> memuat…</td></tr>}
          </tbody>
        </Table>
      </Card>
    </div>
  );
}
